import { ReactNode, useEffect, useRef, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { AxiosError } from 'axios';

import {
  getCounselEntities,
  getSettlementAttyFees,
  updateSettlementAttyFees,
} from '../../../api/casesApi/attyFeesConfigApi';
import { getVendorPaymentMethodDetails } from '../../../api/vendorApi/vendorAccountsApi';
import FormSubmitButton from '../../../components/buttons/FormSubmitButton';
import FormCancelButton from '../../../components/buttons/FormCancelButton';
import FormLinkButton from '../../../components/buttons/FormLinkButton';
import FormSelectField from '../../../components/formFields/FormSelectField';
import FormCurrencyField from '../../../components/formFields/FormCurrencyField';
import FormTextField from '../../../components/formFields/FormTextField';
import FormAutocompleteFieldServerSearch from '../../../components/formFields/FormAutocompleteFieldServerSearch';
import ViewCommonButton from '../../../components/buttons/ViewCommonButton';
import Popup from '../../../components/Popup';
import useSnackBar from '../../../hooks/useSnackBar';
import { useHasPermission } from '../../../hooks/useHasPermission';
import {
  CounselEntityType,
  ErrorData,
  SecondaryFirmRole,
  SettlementAttyFeesSecondaryPayload,
  UpdateSettlementAttyFeesPayload,
  VendorAchAccount,
  VendorCheckAccount,
} from '../../../types';
import { formatWithCurrencyMask } from '../../../helpers/formatWithCurrencyMask';
import { numberToUSDCurrency } from '../../../helpers/numberToUSDCurrency';
import { getVendorPaymentAccountOptions } from '../../../helpers/vendorPaymentAccounts';
import { SECONDARY_FIRM_ROLES_OPTIONS } from '../../../constants/constants';
import { POPUP_DESCRIPTIONS } from '../../../constants/popupDescriptions';
import { settlementAttyFeesFormSchema } from '../../../helpers/formSchemas/formSchemas';

import { mapCounselEntitiesQueryResults } from '../../CasesView/helpers';
import { FIRM_ROLES } from '../../CasesView/types';
import {
  PaymentAccountOption,
  renderPaymentAccountInfo,
  renderPaymentAccountOption,
  renderPaymentAccountSelectedValue,
} from '../paymentAccountDisplayHelpers';

const VENDOR_PAYMENT_METHOD_OPTIONS = [
  { id: 'ACH', label: 'ACH' },
  { id: 'CHECK', label: 'CHECK' },
];

interface SecondaryCounselRow {
  attyFeeId: number;
  id: number;
  counselEntityType: CounselEntityType;
  name: string;
  role: SecondaryFirmRole;
  fee: string;
  paymentMethod: string | null;
  vendorPaymentAccountId: number | string | null;
  memo: string;
}

interface NewCounselValues {
  entity: { id?: string | null; label?: string | null } | null;
  role: string | null;
  fee: string;
  paymentMethod: string;
  paymentAccount: { id?: string | null; label?: string | null } | null;
  memo: string;
}

const isCheckMethod = (method?: string | null) => (method || '').trim().toUpperCase() === 'CHECK';

interface SettlementAttyFeesFormValues {
  leadFirm: { attyFeeId: number; id: number; name: string; fee: string };
  secondaryCounsel: SecondaryCounselRow[];
  newCounsels: NewCounselValues[];
}

const NEW_COUNSEL_DEFAULT_VALUES: NewCounselValues = {
  entity: null,
  role: 'CoCounsel',
  fee: '',
  paymentMethod: '',
  paymentAccount: null,
  memo: '',
};

const FORM_DEFAULT_VALUES: SettlementAttyFeesFormValues = {
  leadFirm: { attyFeeId: 0, id: 0, name: '', fee: '' },
  secondaryCounsel: [],
  newCounsels: [],
};

const parseAmount = (value?: string | null): number => {
  if (!value?.trim()) return NaN;

  return Number(value.replace(/,/g, ''));
};

type VendorPaymentDetails = {
  achAccounts?: VendorAchAccount[];
  checkAccounts?: VendorCheckAccount[];
};

const toAccountOptions = (
  details: VendorPaymentDetails | null | undefined,
  method?: string | null,
): PaymentAccountOption[] => {
  const normalizedMethod = (method || '').trim().toUpperCase();
  const rawAccounts: Array<VendorAchAccount | VendorCheckAccount> =
    (normalizedMethod === 'ACH' ? details?.achAccounts : details?.checkAccounts) || [];

  return getVendorPaymentAccountOptions(details, method).map((item) => {
    const account = rawAccounts.find((acc) => String(acc.id) === String(item.id));
    const subtitle =
      item.paymentMethod === 'ACH' ? item.subtitle.split(' ').slice(-1).join(' ') : item.subtitle;

    return {
      id: item.id,
      label: subtitle ? `${item.label} - ${subtitle}` : item.label,
      subtitle: item.subtitle,
      isLastUsedAccount: account?.isLastUsedAccount,
      isUsedAccount: account?.isUsedAccount,
      isNotUsedAccount: account?.isNotUsedAccount,
      isFailedAccount: account?.isFailedAccount,
    };
  });
};

const renderEntityIcon = (type: CounselEntityType) => (
  <Tooltip title={type === 'VENDOR' ? 'Vendor' : 'Firm'} arrow>
    <Box component="span" display="inline-flex" color="common.white">
      {type === 'VENDOR' ? <StorefrontIcon fontSize="small" /> : <BusinessIcon fontSize="small" />}
    </Box>
  </Tooltip>
);

const toFormAmount = (value?: number | null) =>
  value != null ? formatWithCurrencyMask(value.toFixed(2)) : '';

const useStyles = makeStyles()((theme: Theme) => ({
  feesPageContainer: {
    display: 'flex',
    gap: '16px',
    width: '100%',
    height: '100%',

    '@media (max-width: 1000px)': {
      maxWidth: '600px',
      flexDirection: 'column',
      gap: '30px',
    },
  },

  feesFormWrapper: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    gap: '24px',
    marginTop: '10px',

    '@media (max-width: 1300px)': {
      flexBasis: '600px',
    },

    '@media (max-width: 1000px)': {
      marginTop: '12px',
    },
  },

  feesFormContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    background: theme.palette.primary.light,
    padding: '32px 12px',
    borderRadius: '16px',
    width: '100%',
    boxSizing: 'border-box',
  },

  feesFormFieldsWrapper: {
    display: 'flex',
    gap: '24px',
    alignItems: 'center',

    '@media (max-width: 1300px)': {
      flexDirection: 'column',
    },
  },

  feesFormField: {
    display: 'flex',
    gap: '24px',
    marginBottom: '20px',

    '& > *': {
      flex: 1,
    },

    '@media (max-width: 1300px)': {
      flexDirection: 'column',

      '& > *': {
        width: '100%',
      },
    },
  },

  feesFormButtonsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '16px',

    button: {
      flexBasis: '115px',
    },
  },

  secondaryFirmHeadline: {
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '19.2px',
    color: theme.palette.common.white,
  },

  warningBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: '16px',
    padding: '10px',
    background: '#FF866314',
  },

  warningText: {
    fontFamily: 'Roboto',
    fontSize: '14px',
    color: theme.palette.error.main,
  },

  popupContainer: {
    '& .MuiDialog-paper': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      width: '100%',
      maxWidth: '900px',
      borderRadius: '16px',
      padding: '16px',
      maxHeight: '80vh',
      overflowY: 'auto',
      overflowX: 'hidden',
      boxSizing: 'border-box',

      '&::-webkit-scrollbar': {
        width: '5px',
      },

      '&::-webkit-scrollbar-thumb': {
        borderRadius: '20px',
        backgroundColor: theme.palette.secondary.dark,
      },
    },
  },

  popupTitle: {
    fontFamily: 'Poppins',
    fontSize: '24px',
    fontWeight: 600,
    color: theme.palette.common.white,
    padding: '0 0 4px 0',
  },

  popupSubtitle: {
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.secondary.main,
    padding: '0 0 16px 0',
  },

  popupContent: {
    padding: '10px !important',
    margin: 0,
    overflowX: 'hidden',
  },

  formField: {
    minWidth: '181px',

    '@media (max-width: 1024px)': {
      width: '100%',
    },
  },
}));



interface SettlementAttorneyFeePopupProps {
  isOpen: boolean;
  onClose: () => void;
  settlementId: number;
  onSave?: () => void;
  /** View-only once a payment has gone out (wave 2+). */
  isReadOnly?: boolean;
}

export const SettlementAttorneyFeePopup = ({
  isOpen,
  onClose,
  settlementId,
  onSave,
  isReadOnly = false,
}: SettlementAttorneyFeePopupProps) => {
  const styles = useStyles();
  const { setAlert } = useSnackBar();
  const hasEditPermission = useHasPermission('Cases', 'Edit');
  const canEdit = hasEditPermission && !isReadOnly;
  const canViewVendors = useHasPermission('Vendors', 'View');

  const [total, setTotal] = useState(0);
  const [removedAttyFeeIds, setRemovedAttyFeeIds] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newVendorDetails, setNewVendorDetails] = useState<Record<string, VendorPaymentDetails>>(
    {},
  );
  const [vendorDetailsById, setVendorDetailsById] = useState<Record<string, VendorPaymentDetails>>(
    {},
  );
  const [isFailedAccountConfirmOpen, setIsFailedAccountConfirmOpen] = useState(false);
  const pendingSubmitValuesRef = useRef<SettlementAttyFeesFormValues | null>(null);
  const latestVendorIdRef = useRef<Record<string, string>>({});

  const formMethods = useForm<SettlementAttyFeesFormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(settlementAttyFeesFormSchema),
    defaultValues: FORM_DEFAULT_VALUES,
  });

  const {
    formState: { isValid, isDirty },
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
  } = formMethods;

  const { fields } = useFieldArray({ name: 'secondaryCounsel', control });
  const {
    fields: newFields,
    append: appendNew,
    remove: removeNew,
  } = useFieldArray({ name: 'newCounsels', control });

  const leadFee = watch('leadFirm.fee');
  const secondaryValues = watch('secondaryCounsel');
  const newCounsels = watch('newCounsels');

  const parseEntity = (idx: number) => {
    const [type, id] = String(newCounsels[idx]?.entity?.id || ':').split(':');

    return { type, id };
  };

  const getNewAccountOptions = (idx: number) =>
    toAccountOptions(newVendorDetails[newFields[idx]?.id], newCounsels[idx]?.paymentMethod);

  const getSelectedNewAccount = (idx: number) =>
    getNewAccountOptions(idx).find(
      (item) => item.id === String(newCounsels[idx]?.paymentAccount?.id),
    );

  const loadAttyFees = () => {
    getSettlementAttyFees(settlementId)
      .then((res) => {
        const attorneysFees = res.data?.attorneysFees;

        if (!attorneysFees) return;

        setTotal(attorneysFees.total);
        setRemovedAttyFeeIds([]);
        setNewVendorDetails({});
        setVendorDetailsById({});

        (attorneysFees.secondaryFirms || [])
          .filter((firm) => firm.counselEntityType === 'VENDOR')
          .forEach((firm) => {
            getVendorPaymentMethodDetails(firm.id)
              .then((detailsRes) => {
                setVendorDetailsById((prev) => ({
                  ...prev,
                  [String(firm.id)]: {
                    achAccounts: detailsRes.data.achAccounts,
                    checkAccounts: detailsRes.data.checkAccounts,
                  },
                }));
              })
              .catch(() => undefined);
          });

        reset({
          leadFirm: {
            attyFeeId: attorneysFees.leadFirm.attyFeeId,
            id: attorneysFees.leadFirm.id,
            name: attorneysFees.leadFirm.name,
            fee: toFormAmount(attorneysFees.leadFirm.fee),
          },
          secondaryCounsel: (attorneysFees.secondaryFirms || []).map((firm) => ({
            attyFeeId: firm.attyFeeId,
            id: firm.id,
            counselEntityType: firm.counselEntityType,
            name: firm.name,
            role: firm.role,
            fee: toFormAmount(firm.fees),
            paymentMethod: firm.paymentMethod,
            vendorPaymentAccountId:
              firm.vendorPaymentAccountId != null ? String(firm.vendorPaymentAccountId) : null,
            memo: firm.memo || '',
          })),
          newCounsels: [],
        });
      })
      .catch((error: AxiosError<ErrorData>) => {
        setAlert({
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        });
      });
  };

  useEffect(() => {
    if (!isOpen || !settlementId) return;

    loadAttyFees();
  }, [isOpen, settlementId]);

  const getExistingAccountOptions = (idx: number) => {
    const row = secondaryValues[idx];

    return toAccountOptions(vendorDetailsById[String(row?.id)], row?.paymentMethod);
  };

  const handleExistingPaymentMethodChange = (idx: number, method: string) => {
    const row = secondaryValues[idx];
    const options = getVendorPaymentAccountOptions(vendorDetailsById[String(row?.id)], method);
    const defaultOption = options.find((item) => item.isDefault) || options[0];

    setValue(`secondaryCounsel.${idx}.paymentMethod`, method, { shouldDirty: true });
    setValue(`secondaryCounsel.${idx}.vendorPaymentAccountId`, defaultOption?.id ?? null, {
      shouldDirty: true,
    });
  };

  const handleRemoveExisting = (index: number) => {
    const row = secondaryValues[index];

    if (row?.attyFeeId) {
      setRemovedAttyFeeIds((prev) => [...prev, row.attyFeeId]);
    }

    setValue(
      'secondaryCounsel',
      secondaryValues.filter((_, idx) => idx !== index),
      { shouldDirty: true },
    );
  };

  const handleAddEntry = () => {
    appendNew({ ...NEW_COUNSEL_DEFAULT_VALUES });
  };

  const clearNewVendorDetails = (fieldId: string) => {
    setNewVendorDetails((prev) => {
      const next = { ...prev };

      delete next[fieldId];

      return next;
    });
  };

  const handleCancelNewEntry = (idx: number) => {
    const fieldId = newFields[idx]?.id;

    removeNew(idx);
    clearNewVendorDetails(fieldId);
  };

  const handlePaymentMethodChange = (
    idx: number,
    method: string,
    details: VendorPaymentDetails | null | undefined = newVendorDetails[newFields[idx]?.id],
  ) => {
    setValue(`newCounsels.${idx}.paymentMethod`, method, { shouldDirty: true });

    const options = toAccountOptions(details, method);
    const rawOptions = getVendorPaymentAccountOptions(details, method);
    const defaultId = (rawOptions.find((item) => item.isDefault) || rawOptions[0])?.id;
    const defaultOption = options.find((item) => item.id === defaultId);

    setValue(
      `newCounsels.${idx}.paymentAccount`,
      defaultOption ? { id: defaultOption.id, label: defaultOption.label } : null,
      { shouldDirty: true },
    );
  };

  const handleEntitySelect = async (idx: number, option: { id?: string | null } | null) => {
    const [entityType, entityId] = String(option?.id || ':').split(':');
    const vendorId = entityType === 'VENDOR' ? entityId : '';
    const fieldId = newFields[idx]?.id;

    latestVendorIdRef.current[fieldId] = vendorId;
    clearNewVendorDetails(fieldId);
    setValue(`newCounsels.${idx}.paymentMethod`, '');
    setValue(`newCounsels.${idx}.paymentAccount`, null);

    if (!vendorId) return;

    try {
      const response = await getVendorPaymentMethodDetails(Number(vendorId));

      if (latestVendorIdRef.current[fieldId] !== vendorId) return;

      const details = {
        achAccounts: response.data.achAccounts,
        checkAccounts: response.data.checkAccounts,
      };

      setNewVendorDetails((prev) => ({ ...prev, [fieldId]: details }));

      const defaultMethod = response.data.defaultVendorPaymentMethod;

      if (defaultMethod) {
        handlePaymentMethodChange(idx, defaultMethod, details);
      }
    } catch (error) {
      setAlert({
        message:
          (error as AxiosError<ErrorData>).response?.data.message ||
          'Failed to load vendor payment details.',
        type: 'error',
      });
    }
  };

  const isNewEntriesValid = newCounsels.every((entry, idx) => {
    const { type } = parseEntity(idx);

    return (
      !!entry.entity?.id &&
      !!entry.role &&
      parseAmount(entry.fee) > 0 &&
      (type !== 'VENDOR' || (!!entry.paymentMethod && !!entry.paymentAccount?.id))
    );
  });

  const areAmountsValid =
    parseAmount(leadFee) >= 0 && secondaryValues.every((row) => parseAmount(row.fee) >= 0);

  const hasChanges = isDirty || removedAttyFeeIds.length > 0 || newCounsels.length > 0;

  const getAccountIssue = (
    details: VendorPaymentDetails | null | undefined,
    method: string | null | undefined,
    accountId: string | number | null | undefined,
  ) => {
    const normalizedMethod = (method || '').trim().toUpperCase();

    if (!details || !normalizedMethod) {
      return { isPaymentMethodMissing: false, isPaymentProvisioningIssue: false };
    }

    const rawAccounts: Array<VendorAchAccount | VendorCheckAccount> =
      (normalizedMethod === 'ACH' ? details.achAccounts : details.checkAccounts) || [];
    const selectedRaw = rawAccounts.find((acc) => String(acc.id) === String(accountId));

    return {
      isPaymentMethodMissing: rawAccounts.length === 0,
      isPaymentProvisioningIssue: !!selectedRaw && selectedRaw.provisionStatus !== 'Completed',
    };
  };

  // Same rule as the deduction page: block save while any vendor row shows an account issue
  const hasIssue = (issue: ReturnType<typeof getAccountIssue>) =>
    issue.isPaymentMethodMissing || issue.isPaymentProvisioningIssue;

  const hasAccountIssues =
    secondaryValues.some(
      (row) =>
        row.counselEntityType === 'VENDOR' &&
        hasIssue(
          getAccountIssue(
            vendorDetailsById[String(row.id)],
            row.paymentMethod,
            row.vendorPaymentAccountId,
          ),
        ),
    ) ||
    newCounsels.some(
      (entry, idx) =>
        parseEntity(idx).type === 'VENDOR' &&
        hasIssue(
          getAccountIssue(
            newVendorDetails[newFields[idx]?.id],
            entry.paymentMethod,
            entry.paymentAccount?.id,
          ),
        ),
    );

  const renderAccountIssue = (
    details: VendorPaymentDetails | null | undefined,
    method: string | null | undefined,
    accountId: string | number | null | undefined,
    vendorId: string | number,
  ): ReactNode => {
    const { isPaymentMethodMissing, isPaymentProvisioningIssue } = getAccountIssue(
      details,
      method,
      accountId,
    );

    if (!isPaymentMethodMissing && !isPaymentProvisioningIssue) return null;

    return (
      <Box display="flex" flexDirection="row" alignItems="center" gap="8px" width="100%">
        <Typography color="error" variant="body2" mt="8px">
          {isPaymentMethodMissing
            ? 'Missing Account Details'
            : 'Some issues with the passport provisioning.'}
        </Typography>
        {canViewVendors && <FormLinkButton path={`/vendors/edit-vendor-account/${vendorId}`} />}
      </Box>
    );
  };

  const hasFailedAccountSelected = (values: SettlementAttyFeesFormValues) => {
    const existingFailed = values.secondaryCounsel.some(
      (row, idx) =>
        row.counselEntityType === 'VENDOR' &&
        getExistingAccountOptions(idx).find(
          (item) => item.id === String(row.vendorPaymentAccountId),
        )?.isFailedAccount,
    );

    return (
      existingFailed ||
      newCounsels.some((_, idx) => getSelectedNewAccount(idx)?.isFailedAccount)
    );
  };

  const submitForm = (values: SettlementAttyFeesFormValues) => {
    const secondaryCounsel: SettlementAttyFeesSecondaryPayload[] = [
      ...values.secondaryCounsel.map((row): SettlementAttyFeesSecondaryPayload => ({
        attyFeeId: row.attyFeeId,
        role: row.role,
        feesTotalAmount: parseAmount(row.fee),
        ...(row.counselEntityType === 'VENDOR' && row.paymentMethod && row.vendorPaymentAccountId
          ? {
            paymentMethod: row.paymentMethod,
            vendorPaymentAccountId: Number(row.vendorPaymentAccountId),
            ...(isCheckMethod(row.paymentMethod) ? { memo: row.memo || '' } : {}),
          }
          : {}),
      })),
      ...removedAttyFeeIds.map((attyFeeId): SettlementAttyFeesSecondaryPayload => ({
        attyFeeId,
        delete: true,
      })),
    ];

    values.newCounsels.forEach((entry, idx) => {
      const { type, id } = parseEntity(idx);

      secondaryCounsel.push({
        counselEntityType: type as CounselEntityType,
        id: Number(id),
        role: entry.role as SecondaryFirmRole,
        feesTotalAmount: parseAmount(entry.fee),
        ...(type === 'VENDOR'
          ? {
            paymentMethod: entry.paymentMethod,
            vendorPaymentAccountId: Number(entry.paymentAccount?.id),
            ...(isCheckMethod(entry.paymentMethod) ? { memo: entry.memo || '' } : {}),
          }
          : {}),
      });
    });

    const payload: UpdateSettlementAttyFeesPayload = {
      leadFirm: {
        attyFeeId: values.leadFirm.attyFeeId,
        feesTotalAmount: parseAmount(values.leadFirm.fee),
      },
      secondaryCounsel,
    };

    setIsSubmitting(true);

    updateSettlementAttyFees(settlementId, payload)
      .then(() => {
        setAlert({
          message: 'Attorneys’ fees updated successfully.',
          type: 'success',
        });
        onSave?.();
        onClose();
      })
      .catch((error: AxiosError<ErrorData>) => {
        setAlert({
          message: error.response?.data.message || 'Error. Something went wrong...',
          type: 'error',
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const onSubmitForm = (values: SettlementAttyFeesFormValues) => {
    if (hasFailedAccountSelected(values)) {
      pendingSubmitValuesRef.current = values;
      setIsFailedAccountConfirmOpen(true);
      return;
    }

    submitForm(values);
  };

  const handleConfirmFailedAccountSubmit = () => {
    setIsFailedAccountConfirmOpen(false);

    const values = pendingSubmitValuesRef.current;
    pendingSubmitValuesRef.current = null;

    if (values) submitForm(values);
  };

  const handleCancelFailedAccountSubmit = () => {
    setIsFailedAccountConfirmOpen(false);
    pendingSubmitValuesRef.current = null;
  };

  const feesContent = (
    <Box className={styles.classes.feesPageContainer}>
      <FormProvider {...formMethods}>
        <Box className={styles.classes.feesFormWrapper}>
          <Box className={styles.classes.feesFormContainer}>
            <Box display="flex" alignItems="center" gap="8px">
              {renderEntityIcon('FIRM')}
              <Typography className={styles.classes.secondaryFirmHeadline}>
                {`${watch('leadFirm.name')} - ${FIRM_ROLES.LeadCounsel}`}
              </Typography>
            </Box>

            <Box className={styles.classes.feesFormFieldsWrapper}>
              <FormCurrencyField name="leadFirm.fee" label="Fee" disabled={!canEdit} />
            </Box>
          </Box>

          {fields.map((field, idx) => (
            <Box key={field.id} display="flex" alignItems="center" gap="12px">
              <Box className={styles.classes.feesFormContainer}>
                <Box display="flex" alignItems="center" gap="8px">
                  {renderEntityIcon(field.counselEntityType)}
                  <Typography className={styles.classes.secondaryFirmHeadline}>
                    {field.name}
                  </Typography>
                </Box>

                <Box className={styles.classes.feesFormFieldsWrapper}>
                  <FormSelectField
                    name={`secondaryCounsel.${idx}.role`}
                    label="Role"
                    options={SECONDARY_FIRM_ROLES_OPTIONS}
                    width="100%"
                    disabled={!canEdit}
                  />

                  <FormCurrencyField
                    name={`secondaryCounsel.${idx}.fee`}
                    label="Fee"
                    disabled={!canEdit}
                  />

                  <IconButton
                    sx={{
                      padding: 0,
                      color: 'error.main',
                      '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.08)' },
                    }}
                    onClick={() => handleRemoveExisting(idx)}
                    disabled={!canEdit}
                  >
                    <DeleteForeverIcon />
                  </IconButton>
                </Box>

                {field.counselEntityType === 'VENDOR' && (
                  <Box className={styles.classes.feesFormFieldsWrapper}>
                    <FormSelectField
                      name={`secondaryCounsel.${idx}.paymentMethod`}
                      label="Payment Method"
                      options={VENDOR_PAYMENT_METHOD_OPTIONS}
                      width="100%"
                      customOnChange={(value) => handleExistingPaymentMethodChange(idx, value)}
                      disabled={!canEdit}
                    />

                    <FormSelectField
                      name={`secondaryCounsel.${idx}.vendorPaymentAccountId`}
                      label="Payment Account"
                      options={getExistingAccountOptions(idx)}
                      width="100%"
                      renderSelectedValue={renderPaymentAccountSelectedValue}
                      renderOption={renderPaymentAccountOption}
                      customOnChange={(value) =>
                        setValue(`secondaryCounsel.${idx}.vendorPaymentAccountId`, value, {
                          shouldDirty: true,
                        })
                      }
                      disabled={!canEdit}
                    />

                    <Box width="24px" flexShrink={0} />
                  </Box>
                )}

                {field.counselEntityType === 'VENDOR' &&
                  isCheckMethod(secondaryValues[idx]?.paymentMethod) && (
                    <Box className={styles.classes.feesFormFieldsWrapper}>
                      <FormTextField
                        name={`secondaryCounsel.${idx}.memo`}
                        label="Check Memo"
                        disabled={!canEdit}
                        isMultiline
                        isDescription
                      />
                      <Box width="24px" flexShrink={0} />
                    </Box>
                  )}

                {field.counselEntityType === 'VENDOR' &&
                  renderPaymentAccountInfo(
                    getExistingAccountOptions(idx).find(
                      (item) => item.id === String(secondaryValues[idx]?.vendorPaymentAccountId),
                    ),
                    canViewVendors ? secondaryValues[idx]?.id : null,
                  )}

                {field.counselEntityType === 'VENDOR' &&
                  renderAccountIssue(
                    vendorDetailsById[String(secondaryValues[idx]?.id)],
                    secondaryValues[idx]?.paymentMethod,
                    secondaryValues[idx]?.vendorPaymentAccountId,
                    secondaryValues[idx]?.id,
                  )}
              </Box>
            </Box>
          ))}

          {newFields.map((newField, idx) => {
            const { type: entityType, id: entityId } = parseEntity(idx);
            const isVendor = entityType === 'VENDOR';
            const accountOptions = getNewAccountOptions(idx);

            return (
              <Box
                key={newField.id}
                className={styles.classes.feesFormContainer}
                sx={{ flexDirection: 'row !important', gap: '16px' }}
              >
                <Box flex={1} minWidth={0}>
                  <Box display="flex" alignItems="center" gap="8px" mb="16px">
                    <Typography className={styles.classes.secondaryFirmHeadline}>
                      New Participating Counsel
                    </Typography>
                  </Box>

                  <Box className={styles.classes.feesFormField}>
                    <FormAutocompleteFieldServerSearch
                      name={`newCounsels.${idx}.entity`}
                      label="Firm / Vendor *"
                      width="100%"
                      optionsQuery={getCounselEntities}
                      mapQueryResults={(array) =>
                        mapCounselEntitiesQueryResults(array, watch('leadFirm.id'))
                      }
                      isOptionEqualToValue={(option, value) =>
                        String(option.id) === String(value.id)
                      }
                      onValueChange={(option) => handleEntitySelect(idx, option)}
                      fetchSize={100}
                      minSearchChars={3}
                      showVendorAddressOnHover
                      disabled={!canEdit}
                    />

                    <FormSelectField
                      name={`newCounsels.${idx}.role`}
                      label="Role *"
                      options={SECONDARY_FIRM_ROLES_OPTIONS}
                      width="100%"
                      disabled={!canEdit}
                    />
                  </Box>

                  <Box className={styles.classes.feesFormFieldsWrapper} mb="20px">
                    <FormCurrencyField
                      name={`newCounsels.${idx}.fee`}
                      label="Fee *"
                      disabled={!canEdit}
                    />

                    {isVendor && (
                      <>
                        <FormSelectField
                          name={`newCounsels.${idx}.paymentMethod`}
                          label="Payment Method *"
                          options={VENDOR_PAYMENT_METHOD_OPTIONS}
                          width="100%"
                          customOnChange={(value) => handlePaymentMethodChange(idx, value)}
                          disabled={!canEdit || !newCounsels[idx]?.entity?.id}
                        />

                        <FormSelectField
                          name={`newCounsels.${idx}.paymentAccount.id`}
                          label="Payment Account *"
                          options={accountOptions}
                          width="100%"
                          renderSelectedValue={renderPaymentAccountSelectedValue}
                          renderOption={renderPaymentAccountOption}
                          customOnChange={(value) => {
                            const option = accountOptions.find((item) => item.id === value);

                            setValue(
                              `newCounsels.${idx}.paymentAccount`,
                              option ? { id: option.id, label: option.label } : null,
                              { shouldDirty: true },
                            );
                          }}
                          disabled={!canEdit || !newCounsels[idx]?.paymentMethod}
                        />
                      </>
                    )}
                  </Box>

                  {isVendor && isCheckMethod(newCounsels[idx]?.paymentMethod) && (
                    <Box mb="20px">
                      <FormTextField
                        name={`newCounsels.${idx}.memo`}
                        label="Check Memo"
                        disabled={!canEdit}
                        isMultiline
                        isDescription
                      />
                    </Box>
                  )}

                  {isVendor &&
                    renderPaymentAccountInfo(
                      getSelectedNewAccount(idx),
                      canViewVendors ? entityId : null,
                    )}

                  {isVendor &&
                    renderAccountIssue(
                      newVendorDetails[newField.id],
                      newCounsels[idx]?.paymentMethod,
                      newCounsels[idx]?.paymentAccount?.id,
                      entityId,
                    )}
                </Box>

                <IconButton
                  sx={{
                    padding: 0,
                    color: 'error.main',
                    '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.08)' },
                    '&:hover': { backgroundColor: 'transparent' },
                  }}
                  onClick={() => handleCancelNewEntry(idx)}
                  disabled={!canEdit}
                >
                  <DeleteForeverIcon />
                </IconButton>
              </Box>
            );
          })}

          {canEdit && (
            <Box display="flex" justifyContent="flex-start">
              <ViewCommonButton type="addFirm" onClick={handleAddEntry} />
            </Box>
          )}

          <Box className={styles.classes.feesFormButtonsContainer}>
            {canEdit && (
              <FormCancelButton disabled={isSubmitting || !hasChanges} onClick={loadAttyFees} />
            )}

            {canEdit && (
              <FormSubmitButton
                disabled={
                  !isValid ||
                  !hasChanges ||
                  !areAmountsValid ||
                  !isNewEntriesValid ||
                  hasAccountIssues ||
                  isSubmitting
                }
                buttonIcon={<SaveIcon />}
                buttonText="Save"
                onClick={handleSubmit(onSubmitForm)}
              />
            )}
          </Box>
        </Box>
      </FormProvider>
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      className={styles.classes.popupContainer}
      onClose={onClose}
      scroll="body"
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{
          position: 'sticky',
          top: '-16px',
          zIndex: 3,
          backgroundColor: (theme) => theme.palette.primary.main,
          m: '-16px -16px 0',
          p: '26px 26px 0',
        }}
      >
        <Box>
          <DialogTitle className={styles.classes.popupTitle}>
            Attorney Fees Configuration
          </DialogTitle>

          <Typography className={styles.classes.popupSubtitle}>
            {isReadOnly
              ? 'Fees can no longer be edited once a payment has been made.'
              : 'Edit firm fees, add or remove participating counsel.'}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap="24px">
          <Typography className={styles.classes.secondaryFirmHeadline}>
            {`Total: ${numberToUSDCurrency(total)}`}
          </Typography>
          <IconButton
            disableRipple
            onClick={onClose}
            sx={{ color: 'white', width: '24px', height: '24px' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <DialogContent className={styles.classes.popupContent}>{feesContent}</DialogContent>

      <Popup
        isOpen={isFailedAccountConfirmOpen}
        headlineText={POPUP_DESCRIPTIONS.deductionFailedAccountConfirm.headlineText}
        contentText={POPUP_DESCRIPTIONS.deductionFailedAccountConfirm.contentText}
        mainButtonText="Yes"
        secondaryButtonText="No"
        onMainButtonClick={handleConfirmFailedAccountSubmit}
        onSecondaryButtonClick={handleCancelFailedAccountSubmit}
      />
    </Dialog>
  );
};

export default SettlementAttorneyFeePopup;
